import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DepositSummary = ({ s, testId = 'deposit-summary' }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid={testId}>
    {[['Quote total', s.quote_total, 'total'], ['Deposit required', s.deposit_required, 'required'], ['Deposit paid', s.deposit_paid, 'paid'], ['Remaining balance', s.remaining_balance, 'remaining']].map(([k, v, id]) => (
      <div key={k}><p className="text-neutral-500">{k}</p><p className="font-semibold text-sm" data-testid={`${testId}-${id}`}>{money(v)}</p></div>
    ))}
  </div>
);

// Admin / Super Admin: required online deposit on one quote (percent or fixed)
export const DepositControls = ({ quoteId, token, canEdit }) => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ type: 'percent', value: '50', allow_custom: false });
  const headers = { Authorization: `Bearer ${token}` };
  const load = () => axios.get(`${API}/api/admin/payments/quotes/${quoteId}/deposit`, { headers }).then((r) => { setS(r.data); if (r.data.enabled) setF({ type: r.data.type, value: String(r.data.value), allow_custom: r.data.allow_custom }); }).catch(() => {});
  useEffect(() => { load(); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (remove) => {
    try {
      await axios.put(`${API}/api/admin/payments/quotes/${quoteId}/deposit`, remove ? { type: null } : { ...f, value: parseFloat(f.value) }, { headers });
      toast.success(remove ? 'Deposit request removed' : 'Deposit saved'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save deposit'); }
  };
  if (!s) return null;
  return (
    <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="deposit-controls">
      <p className="text-sm font-medium">Online deposit</p>
      {s.enabled ? <DepositSummary s={s} testId="admin-deposit-summary" /> : <p className="text-xs text-neutral-500">No deposit requested.</p>}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="h-9 border rounded-md px-2" data-testid="deposit-type">
            <option value="percent">Percent of total</option><option value="fixed">Fixed amount ($)</option>
          </select>
          <Input value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="w-28 h-9" data-testid="deposit-value" />
          <label className="flex items-center gap-1 text-xs"><Checkbox checked={f.allow_custom} onCheckedChange={(v) => setF({ ...f, allow_custom: !!v })} data-testid="deposit-allow-custom" /> Let customer choose amount</label>
          <Button size="sm" onClick={() => save(false)} data-testid="deposit-save">Save deposit</Button>
          {s.enabled && <Button size="sm" variant="ghost" onClick={() => save(true)} data-testid="deposit-remove">Remove</Button>}
        </div>
      )}
      <p className="text-[11px] text-neutral-400">The customer pays from the secure customer portal. Paid deposits carry onto the Sales Order and invoice; the balance stays open.</p>
    </div>
  );
};

// Customer portal: pay the requested deposit on an approved quote
export const PortalDeposit = ({ quoteId, headers, invoiced }) => {
  const [s, setS] = useState(null);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/api/customer-portal/quotes/${quoteId}/deposit`, { headers }).then((r) => setS(r.data)).catch(() => {}); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s?.enabled) return null;
  const pay = async () => {
    setBusy(true);
    try {
      const body = { origin_url: window.location.origin, ...(s.allow_custom && custom ? { amount: parseFloat(custom) } : {}) };
      const r = await axios.post(`${API}/api/customer-portal/quotes/${quoteId}/deposit/pay`, body, { headers });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Online card payment is temporarily unavailable. Please contact Apollo Shades for payment options.'); setBusy(false); }
  };
  return (
    <div className="mt-3 border-t pt-3 space-y-2" data-testid={`portal-deposit-${quoteId}`}>
      <DepositSummary s={s} testId={`portal-deposit-summary-${quoteId}`} />
      {invoiced ? <p className="text-xs text-neutral-600" data-testid={`portal-deposit-invoiced-${quoteId}`}>This quote has been invoiced. Please pay the remaining balance from your invoice.</p>
        : s.deposit_due > 0 || (s.allow_custom && s.remaining_balance > 0) ? (
        <div className="flex flex-wrap items-center gap-2">
          {s.allow_custom && <Input placeholder={`Amount (up to ${money(s.remaining_balance)})`} value={custom} onChange={(e) => setCustom(e.target.value)} className="w-48 h-9" data-testid={`portal-deposit-amount-${quoteId}`} />}
          <Button size="sm" onClick={pay} disabled={busy} data-testid={`portal-deposit-pay-${quoteId}`}>{busy ? 'Opening secure payment…' : `Pay deposit ${s.allow_custom && custom ? money(custom) : money(s.deposit_due)}`}</Button>
          <span className="text-[11px] text-neutral-400">Secure card payment by our payment processor</span>
        </div>
      ) : <p className="text-xs text-emerald-700" data-testid={`portal-deposit-done-${quoteId}`}>Deposit received. Thank you!</p>}
    </div>
  );
};
